/**
 * The deterministic business clock (owner: typed-values, PLAN §5 D7).
 *
 * Business code reads time from `clock.now()` / `clock.today(timeZone)`, never from `new Date()`
 * or `Date.now()`. SQL receives the clock as a bound parameter instead of `now()` for business
 * dates. Infrastructure timing (cache TTLs, leases, durations, rate limits) uses `wallClock`, which
 * always follows the machine.
 *
 * Configuration (server processes read it once from the environment when this module loads):
 * - unset `DR_NOW`: wall clock (production).
 * - `DR_NOW=seed`: `DR_SEED_TODAY` (or today in Europe/Berlin) at 09:00 Europe/Berlin.
 * - `DR_NOW=<ISO instant>`: that instant, e.g. `2026-09-29T07:00:00Z`.
 * The pinned clock ticks from process start; `DR_NOW_FROZEN=1` stops it.
 *
 * The web app does not read the environment: it synchronises with `serverNow` from `GET /me`
 * (`clock.syncTo`), so the browser and the API agree on "today".
 */

/** The time zone and hour `DR_NOW=seed` pins the clock to. */
export const SEED_CLOCK_ZONE = 'Europe/Berlin';
export const SEED_CLOCK_HOUR = 9;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export type ClockState = {
  /** `wall` follows the machine; `pinned` was set from DR_NOW (or a test / the server). */
  mode: 'wall' | 'pinned';
  frozen: boolean;
  /** Milliseconds added to the wall clock (0 in `wall` mode). */
  offsetMs: number;
  /** The instant a frozen clock stands at. */
  frozenAtMs: number | null;
};

/** The machine's real time. Use it only for infrastructure timing (TTL, durations, leases). */
export const wallClock = {
  now: (): Date => new globalThis.Date(globalThis.Date.now()),
  nowMs: (): number => globalThis.Date.now(),
};

/** Calendar date (`YYYY-MM-DD`) of an instant in an IANA time zone. */
export function dateInZone(instant: Date | number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(typeof instant === 'number' ? new globalThis.Date(instant) : instant);
}

/** Offset (ms) of a time zone from UTC at an instant: Europe/Berlin in summer → +7_200_000. */
export function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new globalThis.Date(instantMs));
  const get = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUtc = globalThis.Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  return asUtc - (instantMs - (instantMs % 1000));
}

/**
 * The UTC instant of a wall-clock time in a time zone. Times that do not exist (spring forward)
 * move forward by the gap; ambiguous times (fall back) take the earlier instant.
 */
export function zonedWallTimeToInstant(
  wall: { year: number; month: number; day: number; hour?: number; minute?: number; second?: number },
  timeZone: string,
): Date {
  const asUtc = globalThis.Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour ?? 0,
    wall.minute ?? 0,
    wall.second ?? 0,
  );
  // The offsets half a day before and after cover any transition near that wall time.
  const HALF_DAY = 43_200_000;
  const before = asUtc - zoneOffsetMs(asUtc - HALF_DAY, timeZone);
  const after = asUtc - zoneOffsetMs(asUtc + HALF_DAY, timeZone);
  const valid = [before, after].filter((t) => t + zoneOffsetMs(t, timeZone) === asUtc);
  // Ambiguous (both valid): the earlier instant. Skipped (none valid): shift forward by the gap,
  // which is the wall time read with the offset in force before the transition.
  return new globalThis.Date(valid.length ? Math.min(...valid) : before);
}

/** Resolve a `DR_NOW` value to an instant (`seed`, or an ISO instant with offset). */
export function resolveDrNow(value: string, seedToday?: string): Date {
  const v = value.trim();
  if (v === 'seed') {
    const today = seedToday?.trim() || dateInZone(wallClock.now(), SEED_CLOCK_ZONE);
    if (!ISO_DATE.test(today)) throw new Error(`DR_SEED_TODAY must be a date (YYYY-MM-DD), got "${today}"`);
    const [y, m, d] = today.split('-').map(Number) as [number, number, number];
    return zonedWallTimeToInstant({ year: y, month: m, day: d, hour: SEED_CLOCK_HOUR }, SEED_CLOCK_ZONE);
  }
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/.test(v)) {
    throw new Error(`DR_NOW must be "seed" or an ISO instant with offset (2026-09-29T07:00:00Z), got "${v}"`);
  }
  const t = globalThis.Date.parse(v);
  if (Number.isNaN(t)) throw new Error(`DR_NOW is not a valid instant: "${v}"`);
  return new globalThis.Date(t);
}

export class Clock {
  #mode: ClockState['mode'] = 'wall';
  #offsetMs = 0;
  #frozenAt: number | null = null;

  /** The current business instant. */
  now(): Date {
    return new globalThis.Date(this.nowMs());
  }

  nowMs(): number {
    if (this.#frozenAt !== null) return this.#frozenAt;
    return globalThis.Date.now() + this.#offsetMs;
  }

  nowIso(): string {
    return this.now().toISOString();
  }

  /** Today's calendar date (`YYYY-MM-DD`) in a time zone. */
  today(timeZone: string): string {
    return dateInZone(this.nowMs(), timeZone);
  }

  state(): ClockState {
    return {
      mode: this.#mode,
      frozen: this.#frozenAt !== null,
      offsetMs: this.#offsetMs,
      frozenAtMs: this.#frozenAt,
    };
  }

  /** Restore a state captured with `state()`. */
  restore(state: ClockState): void {
    this.#mode = state.mode;
    this.#offsetMs = state.offsetMs;
    this.#frozenAt = state.frozenAtMs;
  }

  isPinned(): boolean {
    return this.#mode === 'pinned';
  }

  /** Pin the clock to an instant; it ticks from now on unless `frozen`. */
  pin(instant: Date | string | number, opts: { frozen?: boolean } = {}): void {
    const at =
      typeof instant === 'number'
        ? instant
        : typeof instant === 'string'
          ? globalThis.Date.parse(instant)
          : instant.getTime();
    if (Number.isNaN(at)) throw new Error(`Invalid clock instant "${String(instant)}"`);
    this.#mode = 'pinned';
    this.#offsetMs = at - globalThis.Date.now();
    this.#frozenAt = opts.frozen ? at : null;
  }

  /**
   * Follow another clock (the web app follows the API): `serverNowMs` was the server's time when
   * the response was received at `receivedAtMs` (local wall time).
   */
  syncTo(serverNowMs: number, receivedAtMs: number, opts: { frozen?: boolean } = {}): void {
    if (!Number.isFinite(serverNowMs) || !Number.isFinite(receivedAtMs)) return;
    this.#mode = 'pinned';
    this.#offsetMs = serverNowMs - receivedAtMs;
    this.#frozenAt = opts.frozen ? serverNowMs : null;
  }

  /** Back to the wall clock. */
  reset(): void {
    this.#mode = 'wall';
    this.#offsetMs = 0;
    this.#frozenAt = null;
  }

  /** Apply `DR_NOW` / `DR_SEED_TODAY` / `DR_NOW_FROZEN`. Throws on an invalid value. */
  configure(env: { DR_NOW?: string; DR_SEED_TODAY?: string; DR_NOW_FROZEN?: string }): void {
    const raw = env.DR_NOW?.trim();
    if (!raw) {
      this.reset();
      return;
    }
    const frozen = /^(1|true)$/i.test(env.DR_NOW_FROZEN?.trim() ?? '');
    this.pin(resolveDrNow(raw, env.DR_SEED_TODAY), { frozen });
  }
}

/** The process-wide business clock. */
export const clock = new Clock();

/** Run `fn` with the clock pinned (tests); restores the previous state afterwards. */
export async function withClock<T>(
  instant: Date | string,
  fn: () => T | Promise<T>,
  opts: { frozen?: boolean } = { frozen: true },
): Promise<T> {
  const before = clock.state();
  clock.pin(instant, opts);
  try {
    return await fn();
  } finally {
    clock.restore(before);
  }
}

// Server processes (api, worker, seed, tests) configure the clock from their environment once.
// Browsers have no `process`; the web app calls `clock.syncTo` with the server's time instead.
const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
if (env?.DR_NOW) clock.configure(env);
